import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { format, parse } from "date-fns";
import Badge from "@shared/v2/Badge";
import Button from "@shared/v2/Button";
import DatePicker from "@shared/v2/DatePicker";
import ErrorBanner from "@shared/v2/ErrorBanner";
import Modal from "@shared/v2/Modal";
import Tab from "@shared/v2/Tab";
import TabBar from "@shared/v2/TabBar";
import TextInput from "@shared/v2/TextInput";
import TextLink from "@shared/v2/TextLink";
import Typography from "@shared/v2/Typography";
import { ArrowUpRightSolidV6, CheckSolidV6 } from "@shared/v2/Icomoon";
import Loader from "../Loader";
import useActivityHistory from "../../services/useActivityHistory";
import ActivityTable from "./ActivityTable";
import useOpenHouseDashboardList from "../../../Reports/components/OpenHouseDashboard/hooks/useOpenHouseDashboardList";
import { Table, TBody, TH, THead, TR } from "../../../Reports/components/BusinessDashboard/Table/Table";
import { ROW_BORDER } from "./ActivityHistoryRow";
import OpenHouseHistoryRow from "./OpenHouseHistoryRow";

const ViewOpenHouseHistoryModal = ({
  endDate,
  goalType,
  ohUsers,
  onClose,
  onSubmit,
  startDate,
  total,
  userUuids,
}) => {
  const [tab, setTab] = useState("openHouseApp");
  const [showLogOpenHouse, setShowLogOpenHouse] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const dateRange = useMemo(() => ({ startAt: startDate, endAt: endDate }), [startDate, endDate]);
  const openHouseDashList = useOpenHouseDashboardList({
    dateRange,
    perPage: 250,
    selectedHosts: goalType ? ohUsers : [],
    status: "completed",
  });
  const { actions, data, loading, error } = useActivityHistory(goalType, userUuids, startDate, endDate);

  return (
    <Modal
      show={Boolean(goalType)}
      closeOnEscape={false}
      onHide={onClose}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[950px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
    >
      <Modal.Header
        className="!tw-space-y-[4px]"
        titleClassName="tw-w-full tw-flex tw-justify-between tw-items-center"
        title={
          <>
            <span>{goalType?.displayName}</span>
            <Button
              size="medium"
              schema="secondary"
              onClick={() => window.open("/agent_stats?report_tab=openHouseDashboard", "_blank")}
            >
              Open House Dashboard <ArrowUpRightSolidV6 />
            </Button>
          </>
        }
        description={<span>Total: {total}</span>}
        closeButton
      />
      <Modal.Body className="tw-flex-1 tw-flex tw-flex-col tw-gap-[16px] tw-overflow-hidden">
        <TabBar>
          <Tab
            label={
              <span className="tw-flex tw-items-center tw-gap-[4px]">
                Open House App
                <Badge
                  className="tw-bg-theme-primary"
                  size="medium"
                  value={openHouseDashList.data.list?.length || 0}
                />
              </span>
            }
            selected={tab === "openHouseApp"}
            onClick={() => setTab("openHouseApp")}
          />
          <Tab
            label={
              <span className="tw-flex tw-items-center tw-gap-[4px]">
                Manually Logged
                <Badge
                  className="tw-bg-theme-primary"
                  size="medium"
                  value={data?.reduce((a, d) => a + Number(d.amount), 0) || 0}
                />
              </span>
            }
            selected={tab === "manuallyLogged"}
            onClick={() => setTab("manuallyLogged")}
          />
        </TabBar>

        {tab === "openHouseApp" && openHouseDashList.data.list?.length > 0 && (
          <Typography className="!tw-text-gray-50">
            Open Houses done though the Brivity Open House App are automatically logged.
          </Typography>
        )}

        {tab === "manuallyLogged" && (
          <>
            <div className="tw-flex tw-items-start tw-gap-[16px]">
              <Typography className="!tw-text-gray-50">
                Only log open houses manually if they weren’t recorded through the Brivity Open House app.
                Open houses captured via the app are logged automatically—no extra steps needed.{" "}
                <TextLink href="https://www.brivityknowledge.com/docs/brivity-open-house-app" target="_blank">
                  Learn how to get started with the app.
                </TextLink>
              </Typography>
              <Button className="tw-whitespace-nowrap" onClick={() => setShowLogOpenHouse(true)}>
                + Log
              </Button>
            </div>
            {showLogOpenHouse && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = Object.fromEntries(new FormData(e.target));
                  const err = {
                    activityDate: !formData.activityDate ? "is required" : "",
                    amount: !formData.amount ? "is required" : "",
                  };

                  setErrors(err);
                  if (!Object.values(err).some((v) => v)) {
                    formData.activityDate = format(
                      parse(formData.activityDate, "MM/dd/yyyy", new Date()),
                      "yyyy-MM-dd",
                    );
                    if (!formData.noteContent.trim()) delete formData.noteContent;
                    setSaving(true);
                    onSubmit(formData).then(() => {
                      setSaving(false);
                      setShowLogOpenHouse(false);
                      actions.reload();
                    });
                  }
                }}
                className="tw-border-solid tw-border-[1px] tw-border-gray-5 tw-rounded-[8px] tw-p-[16px] tw-flex tw-flex-col tw-gap-[16px]"
              >
                <input type="hidden" name="goalTypeId" value={goalType?.id} />
                <div className="tw-flex tw-flex-wrap tw-gap-[16px]">
                  <DatePicker
                    containerClassName="tw-flex-1 tw-min-w-[140px]"
                    inputProps={{
                      label: "Date",
                      isRequired: true,
                      size: 1,
                      name: "activityDate",
                      error: errors.activityDate,
                    }}
                  />
                  <TextInput
                    containerClassName="tw-flex-1 tw-min-w-[140px]"
                    label="# of Open Houses"
                    name="amount"
                    isRequired
                    size={1}
                    error={errors.amount}
                    type="number"
                  />
                  <TextInput
                    containerClassName="tw-flex-1 tw-basis-1/3"
                    className="tw-max-h-[80px] tw-resize-none"
                    style={{ fieldSizing: "content" }}
                    label="Notes"
                    name="noteContent"
                    multiline
                    rows="1"
                  />
                </div>
                <div className="tw-flex tw-justify-between tw-gap-[16px]">
                  <Button schema="tertiary" onClick={() => setShowLogOpenHouse(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={saving}>
                    Save
                  </Button>
                </div>
              </form>
            )}
          </>
        )}

        <div className="tw-flex-1 tw-overflow-auto">
          {tab === "openHouseApp" && (
            <>
              {openHouseDashList.data.list?.length > 0 && (
                <Table className="tw-border-collapse">
                  <THead>
                    <TR>
                      <TH className={ROW_BORDER}>Address</TH>
                      <TH className={ROW_BORDER}>Date & Time</TH>
                      <TH className={ROW_BORDER}>Host</TH>
                      <TH className={ROW_BORDER}>Attendees</TH>
                      <TH className={ROW_BORDER}>Leads</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {openHouseDashList.data.list?.map((activity) => (
                      <OpenHouseHistoryRow key={activity.uuid} activity={activity} />
                    ))}
                  </TBody>
                </Table>
              )}
              {openHouseDashList.data.list?.length === 0 && (
                <div className="tw-px-[8px] tw-pt-[40px] tw-pb-[8px] tw-flex tw-flex-col tw-gap-[40px]">
                  <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-start tw-gap-[48px]">
                    <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[24px]">
                      <Typography variant="headline2" className="!tw-text-gray-50">
                        Streamline Your Open House Lead Capture{" "}
                        <span className="tw-font-normal">with the Brivity Open House App</span>
                      </Typography>
                      <div className="tw-text-gray-50">
                        <div className="tw-flex tw-items-center tw-gap-[6px]">
                          <CheckSolidV6 className="tw-text-theme-primary" /> Automatically capture attendees
                          in your CRM
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-[6px]">
                          <CheckSolidV6 className="tw-text-theme-primary" /> Eliminate the need for manual
                          entry
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-[6px]">
                          <CheckSolidV6 className="tw-text-theme-primary" /> Ensure no lead is missed
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-[6px]">
                          <CheckSolidV6 className="tw-text-theme-primary" /> Automate lead follow up
                        </div>
                      </div>
                    </div>
                    <img
                      className="tw-flex-1 tw-w-full"
                      src="/images/dash-open-house-empty.png"
                      alt="Charts and Data"
                    />
                  </div>
                  <div className="tw-px-[8px] tw-py-[16px]">
                    <div className="tw-shadow-dropdown tw-rounded-[12px] tw-p-[24px] tw-flex tw-justify-between tw-items-center">
                      <Typography variant="headline3">Create your first open house</Typography>
                      <Button
                        size="medium"
                        onClick={() => window.open("/agent_stats?report_tab=openHouseDashboard", "_blank")}
                      >
                        Get Started <ArrowUpRightSolidV6 />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {tab === "manuallyLogged" && (
            <>
              {data?.length > 0 && (
                <ActivityTable
                  data={data}
                  onDelete={actions.deleteActivity}
                  onUpdate={actions.updateActivity}
                />
              )}
              {data?.length === 0 && (
                <p className="tw-text-center tw-py-[40px]">You have not logged any open houses.</p>
              )}
            </>
          )}

          {error && <ErrorBanner errors={["Whoops! Something went wrong."]} />}
          {loading && <Loader />}
        </div>
      </Modal.Body>
    </Modal>
  );
};

ViewOpenHouseHistoryModal.propTypes = {
  endDate: PropTypes.string.isRequired,
  goalType: PropTypes.shape(),
  ohUsers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  total: PropTypes.number,
  userUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ViewOpenHouseHistoryModal.defaultProps = {
  goalType: null,
  total: 0,
};

export default ViewOpenHouseHistoryModal;
